import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Save } from 'lucide-react';

const EditSessionModal = ({ session, isLoading, error, onSave, onClose }) => {
  // Une séance partie (en cours, terminée) ne change plus que de titre : sa date
  // est celle de la passation, c'est elle qui date la note (2026-10-04).
  const dateModifiable = session.statut === 'en_attente';
  const [titre, setTitre] = useState(session.titre);
  const [datePassation, setDatePassation] = useState(session.date_passation || '');

  const hasChanges = useMemo(() => {
    if (titre !== session.titre) return true;
    if (dateModifiable && (datePassation || null) !== (session.date_passation || null)) return true;
    return false;
  }, [titre, datePassation, session, dateModifiable]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!hasChanges || isLoading) return;
    onSave(dateModifiable
      ? { titre: titre.trim(), date_passation: datePassation || null }
      : { titre: titre.trim() });
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={(e) => e.stopPropagation()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-800">Modifier la session</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
            <input
              type="text"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 focus:ring-2 focus:ring-blue-300/50 focus:border-blue-300 focus:outline-none"
              required
            />
          </div>

          {dateModifiable ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de passation</label>
              <input
                type="date"
                value={datePassation}
                onChange={(e) => setDatePassation(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 focus:ring-2 focus:ring-blue-300/50 focus:border-blue-300 focus:outline-none"
              />
              <p className="text-xs text-gray-400 mt-1">Laissez vide pour une session sans date programmée</p>
            </div>
          ) : null}

          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!hasChanges || isLoading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${
                hasChanges && !isLoading
                  ? 'bg-indigo-500 hover:bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Save size={16} />
              {isLoading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default EditSessionModal;